import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BarRows from '../components/charts/BarRows';
import { ErrorBanner, InfoBanner } from '../components/Feedback';
import { api } from '../api/client';
import { absent } from '../absent';
import { useAuth } from '../context/AuthContext';

/**
 * Module 12 — the Support dashboard: the review queue and how long people have been
 * waiting in it.
 *
 * <p>Ageing leads rather than the raw count, because a queue of forty processed within
 * a day is healthy and a queue of three where one has waited a fortnight is not.
 */
export default function SupportDashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.supportDashboard().then(setData).catch(setError);
  }, []);

  return (
    <>
      <div className="page-head">
        <h1>Dashboard</h1>
        <p>Signed in as {user?.roleLabel}. Teachers waiting on a decision, oldest first.</p>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />
      {!data && !error && <div className="empty">Loading…</div>}

      {data && (
        <>
          <div className="kpi-row">
            <Kpi label="Awaiting a decision" value={data.awaitingDecision}
                 note={data.awaitingDecision === 0 ? 'Queue is clear' : 'Across all three stages'} />
            <Kpi label="Pending review" value={data.pending} note="Not yet picked up" />
            <Kpi label="Under review" value={data.underReview} note="Documents being checked" />
            <Kpi
              label="Longest wait"
              value={data.oldestWaitingDays ?? 0}
              note={absent(data.oldestWaitingDays) ? 'Nobody waiting' : 'days, oldest in the queue'}
            />
          </div>

          {data.unlinkedSchools > 0 && (
            <InfoBanner>
              <strong>{data.unlinkedSchools}</strong> teacher
              {data.unlinkedSchools === 1 ? ' is' : 's are'} not linked to a school in the
              directory, so they cannot receive activation codes. Open each in{' '}
              <Link to="/verification">Teacher verification</Link> and use <b>Link to a school</b>.
            </InfoBanner>
          )}

          <div className="panel-grid">
            <section className="card card-pad panel">
              <h2>How long they have waited</h2>
              <p className="panel-sub">
                Everyone still awaiting a decision, bucketed by age. One person stuck for
                a fortnight is what an average would hide.
              </p>
              <BarRows
                slices={data.ageing.map((bucket) => ({
                  key: bucket.label,
                  label: bucket.label,
                  value: bucket.count,
                }))}
                emptyMessage="Nobody is waiting."
              />
              <p className="hint">
                <Link to="/verification">Open the review queue</Link>
              </p>
            </section>

            <section className="card card-pad panel">
              <h2>Where the queue sits</h2>
              <p className="panel-sub">The three stages a teacher passes through, plus rejections.</p>
              <BarRows
                slices={[
                  { key: 'PENDING', label: 'Pending', value: data.pending },
                  { key: 'UNDER_REVIEW', label: 'Under review', value: data.underReview },
                  { key: 'ASSIGNED_TO_SALES', label: 'Assigned to sales', value: data.assignedToSales },
                  { key: 'REJECTED', label: 'Rejected', value: data.rejected },
                ]}
                emptyMessage="No teachers have registered yet."
              />
              <p className="hint">{data.registeredLast7Days} registered in the last 7 days.</p>
            </section>
          </div>

          <p className="tiny muted" style={{ marginTop: 'var(--s2)' }}>
            Counted at {new Date(data.generatedAt).toLocaleTimeString()}.
          </p>
        </>
      )}
    </>
  );
}

function Kpi({ label, value, note }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value.toLocaleString()}</div>
      {note && <div className="kpi-note">{note}</div>}
    </div>
  );
}
